import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router';
import * as RestApi from 'core/network/RestApi';
import { ITemplate } from 'entities/template/template';
import useNotifications from 'core/notifications/useNotifications';
import { restoreTemplateVersion } from 'sections/design/template-management/sections/template-editor/utils/queries';
import { TemplateApiEndpoints } from 'sections/design/template-management/constants/api-endpoints';
import { useRcsTemplateEditorDispatch, useRcsTemplateEditorState } from '../context/useRcsTemplateEditorContext';
import { templateBodyToRcsConfig } from '../utils/rcsConfigToCardContent';

export function useRestoreRcsTemplateVersion() {
  const { t } = useTranslation('designs');
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();
  const dispatch = useRcsTemplateEditorDispatch();
  const { previewingVersion: versionId } = useRcsTemplateEditorState();

  const { siteId, id: templateId, language } = useParams<{
    siteId: string;
    id: string;
    language: string;
  }>();

  const restoreMutation = useMutation(restoreTemplateVersion, {
    onSuccess: async () => {
      showNotification(t('designGallery.templateEditor.queries.restore.success'), 'normal');
      dispatch({ type: 'SET_TEMPLATE_VERSION', payload: { versionId: '' } });
      dispatch({ type: 'SET_EDITOR_SETTINGS', payload: { readOnly: false } });
      queryClient.invalidateQueries({ queryKey: ['templateHistory'] });
      if (siteId && templateId && language) {
        queryClient.invalidateQueries({ queryKey: ['template', siteId, templateId, language] });
        try {
          const result = await RestApi.get<ITemplate>(
            generatePath(TemplateApiEndpoints.GET_TEMPLATE, { siteId, templateId, language }),
          );
          const config = templateBodyToRcsConfig(result.data?.template);
          if (config) {
            dispatch({
              type: 'SET_RCS_CONFIG',
              payload: { ...config, hasUnsavedChanges: false },
            });
            dispatch({ type: 'SET_LAST_SAVED_RCS_CONFIG', payload: { rcsConfig: config } });
          }
        } catch (err) {
          // eslint-disable-next-line no-console -- refetch failure should be visible for debugging
          console.error('Failed to refetch template after restore', err);
        }
      }
    },
    onError: (error: Error) => {
      showNotification(error.message, 'error');
    },
  });

  const triggerRestore = useCallback(() => {
    if (!siteId || !templateId || !language || !versionId) return;
    restoreMutation.mutate({ siteId, templateId, language, versionId, t });
  }, [siteId, templateId, language, versionId, t, restoreMutation]);

  return { restoreTemplateVersion: triggerRestore, isRestoring: restoreMutation.isLoading };
}
